import React, { useMemo, useState } from 'react';
import { Edit3, Plus, Save } from 'lucide-react';
import { money, saveAdminCoupon, toggleAdminCoupon } from '../lib/compat.js';
import { AdminFilterBar, AdminModal, EmptyState, Pager, usePagedRows } from './shared.jsx';

export default function AdminCoupons({ data, query, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const needle = query.trim().toLowerCase();
  const rows = useMemo(() => data.coupons.filter((c) => !needle || [c.id, c.type, c.desc, c.active ? 'active' : 'inactive'].join(' ').toLowerCase().includes(needle)), [data.coupons, needle]);
  const pager = usePagedRows(rows, 20);

  async function toggle(coupon) {
    await toggleAdminCoupon(coupon.id, coupon.active === false);
    await onRefresh();
  }

  return (
    <>
      <AdminFilterBar>
        <button type="button" className="btn-green" onClick={() => setEditing({ type: 'percent', active: true })}>
          <Plus size={14} /> Add Coupon
        </button>
      </AdminFilterBar>

      <div className="adminCard">
        <div className="adminCardHead"><h3>Coupon Codes</h3><span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{rows.length} coupon(s)</span></div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr><th>Code</th><th>Type</th><th>Discount</th><th>Description</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {pager.paged.map((coupon) => (
                <tr key={coupon.id}>
                  <td><strong style={{ fontFamily: 'monospace', letterSpacing: '.05em' }}>{coupon.id}</strong></td>
                  <td><span className="badge" style={{ background: coupon.type === 'percent' ? '#EDE9FE' : '#E8F5EE', color: coupon.type === 'percent' ? '#5B21B6' : '#1a7a3e' }}>{coupon.type}</span></td>
                  <td><strong style={{ fontSize: 15 }}>{coupon.type === 'percent' ? `${coupon.value}%` : money(coupon.value)}</strong></td>
                  <td style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{coupon.desc || '—'}</td>
                  <td>
                    <span className={`badge ${coupon.active === false ? 'b-canc' : 'b-active'}`}>{coupon.active === false ? 'Inactive' : 'Active'}</span>
                  </td>
                  <td>
                    <div className="rowActions">
                      <button type="button" onClick={() => setEditing(coupon)}><Edit3 size={13} /> Edit</button>
                      <button type="button" onClick={() => toggle(coupon)} style={{ color: coupon.active === false ? 'var(--admin-green)' : 'var(--admin-muted)' }}>
                        {coupon.active === false ? 'Activate' : 'Pause'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <EmptyState title="No coupons found" body="Create your first coupon above." />}
        </div>
        <Pager state={pager} total={rows.length} />
      </div>

      {editing && (
        <CouponModal coupon={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onRefresh(); }} />
      )}
    </>
  );
}

function CouponModal({ coupon, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: coupon.id || '',
    type: coupon.type || 'percent',
    value: coupon.value || '',
    desc: coupon.desc || '',
    active: coupon.active !== false
  });
  const [msg, setMsg] = useState('');

  async function save() {
    try {
      await saveAdminCoupon({ id: form.code, ...form });
      await onSaved();
    } catch (e) {
      setMsg(e.message || 'Unable to save coupon.');
    }
  }

  return (
    <AdminModal title={coupon.id ? 'Edit Coupon' : 'Add Coupon'} onClose={onClose}>
      {msg && <div className="banner danger compact">{msg}</div>}
      <div style={{ background: '#FFFDE7', border: '1px solid #F0C040', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#7A5C00', marginBottom: 12 }}>
        💡 Coupon codes are case-insensitive and saved in uppercase.
      </div>
      <div className="adminFormGrid">
        <label>
          Code *
          <input value={form.code} disabled={!!coupon.id} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })} placeholder="WELCOME10" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
        </label>
        <label>
          Type
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="percent">Percent (%)</option>
            <option value="flat">Flat Amount (₹)</option>
          </select>
        </label>
        <label>
          Discount Value *
          <input type="number" value={form.value} min={1} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === 'percent' ? '10' : '50'} />
          <small style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{form.type === 'percent' ? 'Percentage off (1–100)' : 'Fixed rupees off'}</small>
        </label>
        <label className="full">
          Description
          <input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="e.g. 10% off all books" />
        </label>
      </div>
      <div className="modalActions">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active
        </label>
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save}><Save size={14} /> Save Coupon</button>
      </div>
    </AdminModal>
  );
}
